import React, { useEffect, useState } from 'react';
import { Form, Input, DatePicker, Switch, Button, Space, Select, Upload, Alert, Checkbox, InputNumber, Modal, message, Row, Col, Tooltip, Spin } from 'antd';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import PropTypes from 'prop-types';
import { ocrService } from '../../../services/ocrService';
import dayjs from 'dayjs';
import { studentService } from '../../../services/studentService';

const { Option } = Select;
const { TextArea } = Input;
const { Group: CheckboxGroup } = Checkbox;

// Constants
const namePattern = /^[\u4e00-\u9fa5]{2,10}$/;
const idCardPattern = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/;
const phonePattern = /^1[3-9]\d{9}$/;


const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.bmp,.webp,.tiff,.heic';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Exam options
const examOptions = [
  { label: '科目一', value: 'exam1' },
  { label: '科目二', value: 'exam2' },
  { label: '科目三', value: 'exam3' },
  { label: '科目四', value: 'exam4' },
];

const StudentForm = ({ open, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [isTransferred, setIsTransferred] = useState(false);
  const [receiptFileList, setReceiptFileList] = useState([]);
  const [tuitionReceiptList, setTuitionReceiptList] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('微信');
  const [examStates, setExamStates] = useState({
    exam1: false,
    exam2: false,
    exam3: false,
    exam4: false
  });
  const [submitting, setSubmitting] = useState(false);

  // Set initial values when form opens
  useEffect(() => {
    if (open) {
      resetForm('all');
    }
  }, [open]);

  // Reset form function with optional step parameter
  const resetForm = (step = 'all') => {
    console.log('Resetting form for step:', step);
    
    // Reset file lists and previews
    if (step === 'all' || step === 2) {
      setReceiptFileList([]);
      setTuitionReceiptList([]);
      setPaymentMethod('微信');
      setPreviewImage(null);
      setPreviewOpen(false);
      setPreviewTitle('');
      // Reset exam states when resetting step 2
      setExamStates({
        exam1: false,
        exam2: false,
        exam3: false,
        exam4: false
      });
      setIsTransferred(false);
    }

    // Reset other states
    if (step === 'all') {
      setCurrentStep(1);
      setIsTransferred(false);
      setExamStates({
        exam1: false,
        exam2: false,
        exam3: false,
        exam4: false
      });
    }

    // Reset form fields based on step
    if (step === 'all') {
      form.resetFields(); // Reset all fields
      // Set default values
      form.setFieldsValue({
        signUpDate: dayjs(),
        paymentMethod: '微信',
        VIP: false,
        transferred: false,
        ethnicity: '汉族',
        gender: '男',
        local: true,
        interviewed: true
      });
    } else if (step === 1) {
      // Reset only step 1 fields
      form.resetFields([
        'name', 'uniqueId', 'phone', 'address', 
        'gender', 'ethnicity', 'birthday', 'local'
      ]);
    } else if (step === 2) {
      // Reset only step 2 fields
      form.resetFields([
        'signUpDate', 'VIP', 'transferred', 'interviewed',
        'receiptNumber', 'receiptPic',
        'paymentMethod', 'tuitionFee', 
        'tuitionReceipt', 'referrer', 'notes',
        // Add exam-related fields
        'exam1Passed', 'exam1Date',
        'exam2Passed', 'exam2Date',
        'exam3Passed', 'exam3Date',
        'exam4Passed', 'exam4Date'
      ]);
      form.setFieldsValue({
        signUpDate: dayjs(),
        paymentMethod: '微信',
        interviewed: true,
        transferred: false
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }

    setSubmitting(true);
    try {
      const step1Data = form.getFieldsValue([
        'name', 'uniqueId', 'phone', 'address', 
        'gender', 'ethnicity', 'birthday', 'local'
      ]);

      const formData = {
        ...step1Data,
        ...values,
        local: step1Data.local ?? true,
        // Convert file lists to arrays of file objects for upload
        receiptPic: receiptFileList.map(file => file.originFileObj).filter(Boolean),
        tuitionReceipt: tuitionReceiptList.map(file => file.originFileObj).filter(Boolean)
      };

      await studentService.createStudent(formData);
      message.success('添加学员成功');
      resetForm('all');
      onSuccess?.();
    } catch (error) {
      console.error('Form submission error:', error);
      message.error('提交失败：' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset button handler
  const handleReset = () => {
    resetForm(currentStep); // Reset current step only
  };

  // Extract info from ID card
  const handleIdCardChange = (value) => {
    if (idCardPattern.test(value)) {
      const birth = value.substring(6, 14);
      const birthDate = moment(birth, 'YYYYMMDD');
      const gender = parseInt(value.substr(16, 1)) % 2 === 1 ? '男' : '女';

      form.setFieldsValue({
        birthday: birthDate.format('YYYY-MM-DD'),
        gender: gender
      });
    }
  };

  const handleIDCardUpload = async (file) => {
    if (!file) return false;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('请上传图片文件！');
      return false;
    }

    // Validate file size (less than 4MB)
    if (file.size > 4 * 1024 * 1024) {
      message.error('图片大小不能超过4MB！');
      return false;
    }

    setUploading(true);
    try {
      // Show preview
      setPreviewImage(URL.createObjectURL(file));

      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
      reader.readAsDataURL(file);
      
      const base64 = await base64Promise;
      const imageBase64 = base64.split(',')[1]; // Remove data:image/jpeg;base64,
      
      // Process with Baidu OCR
      const ocrResult = await ocrService.processIDCard(imageBase64);
      
      if (ocrResult.error_code) {
        throw new Error(ocrResult.error_msg || '身份证识别失败');
      }

      // Map OCR result to form fields
      const formData = {
        name: ocrResult.words_result?.姓名?.words,
        uniqueId: ocrResult.words_result?.公民身份号码?.words,
        address: ocrResult.words_result?.住址?.words,
        ethnicity: ocrResult.words_result?.民族?.words,
        gender: ocrResult.words_result?.性别?.words,
      };

      // Validate required fields
      if (!formData.name || !formData.uniqueId) {
        throw new Error('无法识别必要的身份信息，请重试或手动输入');
      }

      // Set form values and trigger ID card validation
      form.setFieldsValue(formData);
      if (formData.uniqueId) {
        handleIdCardChange(formData.uniqueId);
      }

      message.success('身份证信息识别成功');
    } catch (error) {
      console.error('OCR Error:', error);
      message.error(error.message || '身份证识别失败，请重试或手动输入');
      setPreviewImage(null); // Clear preview on error
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };


  // Upload button component
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>上传</div>
    </div>
  );

  // File preview handler
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  // File upload validation
  const beforeUpload = (file) => {
    console.log('Validating file before upload:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    // Check file extension
    const extension = `.${file.name.split('.').pop().toLowerCase()}`;
    const allowedExtList = ALLOWED_EXTENSIONS.split(',');
    if (!allowedExtList.includes(extension)) {
      message.error(`只能上传以下格式的图片: ${ALLOWED_EXTENSIONS}`);
      return Upload.LIST_IGNORE;
    }
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      message.error('图片大小不能超过 5MB!');
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto upload
  };

  // Upload handlers
  const handleReceiptChange = ({ fileList: newFileList }) => {
    console.log('Receipt file changed:', newFileList);
    console.log('Receipt file details:', newFileList[0]?.originFileObj);
    setReceiptFileList(newFileList);
  };

  const handleTuitionReceiptChange = ({ fileList: newFileList }) => {
    console.log('Tuition receipt changed:', newFileList);
    console.log('Tuition receipt details:', newFileList[0]?.originFileObj);
    setTuitionReceiptList(newFileList);
  };

  // Update to handle individual exam passed states
  const handleExamPassedChange = (exam, checked) => {
    setExamStates(prev => ({
      ...prev,
      [exam]: checked
    }));
    
    if (!checked) {
      form.setFieldValue(`${exam}Date`, null);
    }
  };

  // Helper function for file conversion
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
    

  const renderStep1 = () => (
    <>
      
        <Alert
          message="提示"
          description="您可以选择上传身份证照片自动识别信息，或手动填写以下信息。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      {/* ID Card Upload Section */}
      
        <Form.Item label="身份证照片">
          <Upload
            accept="image/*"
            beforeUpload={handleIDCardUpload}
            showUploadList={false}
          >
            <Button 
              icon={<UploadOutlined />} 
              loading={uploading}
            >
              {uploading ? '正在识别...' : '上传身份证照片'}
            </Button>
          </Upload>
          {previewImage && (
            <div style={{ marginTop: 8 }}>
              <img 
                src={previewImage} 
                alt="身份证预览" 
                style={{ maxWidth: '100%', maxHeight: 200 }}
              />
            </div>
          )}
        </Form.Item>
    

      {/* Basic Information Fields */}
      <Form.Item
        name="name"
        label="姓名"
        rules={[
          { required: true, message: '请输入姓名' },
          { pattern: namePattern, message: '请输入2-10个汉字' }
        ]}
      >
        <Input placeholder="请输入姓名" />
      </Form.Item>

      <Form.Item
        name="uniqueId"
        label="身份证号"
        rules={[
          { required: true, message: '请输入身份证号' },
          { pattern: idCardPattern, message: '请输入正确的身份证号' }
        ]}
      >
        <Input 
          onChange={(e) => handleIdCardChange(e.target.value)}
          placeholder="请输入身份证号"
        />
      </Form.Item>

      <Form.Item
        name="phone"
        label="手机号"
        rules={[
          { required: true, message: '请输入手机号' },
          { pattern: phonePattern, message: '请输入正确的手机号' }
        ]}
      >
        <Input placeholder="请输入手机号" />
      </Form.Item>

      <Row gutter={24}>
        <Col span={18}>
          <Form.Item name="address" label="地址">
            <Input.TextArea placeholder="请输入地址" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="local" 
            label="本地学员"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="gender" label="性别">
        <Select placeholder="请选择性别">
          <Option value="男">男</Option>
          <Option value="女">女</Option>
        </Select>
      </Form.Item>

      <Form.Item 
        name="ethnicity" 
        label="民族"
        initialValue="汉族"
      >
        <Input placeholder="请输入民族" />
      </Form.Item>

      {/* Hidden fields */}
      <Form.Item name="birthday" label="出生日期" hidden>
        <Input />
      </Form.Item>
    </>
  );

  const handleTransferChange = (checked) => {
    setIsTransferred(checked);
    form.setFieldsValue({ transferred: checked });
    
    // If student is transferred, they must have documents
    if (checked) {
      console.log('Student is transferred, documents must exist');
    }
  };

  const renderStep2 = () => (
    <>
      <Form.Item
        name="signUpDate"
        label="报名日期"
        rules={[{ required: true, message: '请选择报名日期' }]}
      >
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Row gutter={24}>
        <Col span={12}>
          <Form.Item 
            name="VIP" 
            label="VIP" 
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item 
            name="interviewed" 
            label="已面签" 
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item 
        name="transferred" 
        label="转入" 
        valuePropName="checked"
      >
        <Switch onChange={handleTransferChange} />
      </Form.Item>

      {isTransferred && (
        <>
          {examOptions.map(({ label, value }) => (
            <React.Fragment key={value}>
              <Form.Item
                name={`${value}Passed`}
                label={`${label}通过`}
                valuePropName="checked"
              >
                <Switch onChange={(checked) => handleExamPassedChange(value, checked)} />
              </Form.Item>

              {examStates[value] && (
                <Form.Item
                  name={`${value}Date`}
                  label={`${label}考试日期`}
                  rules={[{ required: true, message: '请选择考试日期' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              )}
            </React.Fragment>
          ))}
        </>
      )}


      <Form.Item
        name="receiptNumber"
        label="收据号"
        rules={[{ required: true, message: '请输入收据号' }]}
      >
        <Input placeholder="请输入收据号" />
      </Form.Item>

      <Form.Item
        name="receiptPic"
        label="收据照片"
        rules={[{ required: true, message: '请上传收据照片' }]}
        extra="支持上传多张照片，最多5张"
      >
        <Upload
          accept={ALLOWED_EXTENSIONS}
          listType="picture-card"
          fileList={receiptFileList}
          onPreview={handlePreview}
          beforeUpload={beforeUpload}
          onChange={handleReceiptChange}
          multiple={true}
          maxCount={5}
        >
          {receiptFileList.length >= 5 ? null : uploadButton}
        </Upload>
      </Form.Item>

      <Form.Item
        name="paymentMethod"
        label="交款方式"
        rules={[{ required: true, message: '请选择交款方式' }]}
      >
        <Select onChange={setPaymentMethod}>
          <Option value="未交款">未交款</Option>
          <Option value="现金">现金</Option>
          <Option value="微信">微信</Option>
          <Option value="支付宝">支付宝</Option>
        </Select>
      </Form.Item>

      {paymentMethod !== '未交款' && (
        <>
          <Form.Item
            name="tuitionFee"
            label="学费"
            rules={[{ required: true, message: '请输入学费金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `￥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/￥\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="tuitionReceipt"
            label="支付凭证"
            rules={[{ required: paymentMethod !== '现金', message: '请上传支付凭证' }]}
            extra="支持上传多张照片，最多5张"
          >
            <Upload
              accept={ALLOWED_EXTENSIONS}
              listType="picture-card"
              fileList={tuitionReceiptList}
              onPreview={handlePreview}
              beforeUpload={beforeUpload}
              onChange={handleTuitionReceiptChange}
              multiple={true}
              maxCount={5}
            >
              {tuitionReceiptList.length >= 5 ? null : uploadButton}
            </Upload>
          </Form.Item>
        </>
      )}

      <Form.Item name="referrer" label="推荐人">
        <Input placeholder="请输入推荐人姓名" />
      </Form.Item>

      <Form.Item name="notes" label="备注">
        <TextArea rows={4} placeholder="请输入备注信息" />
      </Form.Item>
    </>
  );

  const handleCancel = () => {
    console.log('Cancel button clicked');
    resetForm('all'); // Reset everything when closing
    if (onCancel) {
      console.log('Executing onCancel callback');
      onCancel();
    }
  };

  // Render form footer
  const renderFooter = () => (
    <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
      <Space>
        <Button onClick={handleReset}>重置</Button>
        <Button onClick={handleCancel}>取消</Button>
        <Button 
          type="primary" 
          htmlType="submit"
          loading={submitting}
        >
          {currentStep === 1 ? '下一步' : '完成'}
        </Button>
      </Space>
    </Form.Item>
  );

  return (
    <Modal
      title="新增学员"
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Spin spinning={submitting} tip="正在提交...">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {currentStep === 1 ? renderStep1() : renderStep2()}
          {renderFooter()}
        </Form>
      </Spin>
    </Modal>
  );
};

StudentForm.propTypes = {
  open: PropTypes.bool,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func
};

export default StudentForm; 